import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSetSnackbarGlobalError, useSetSnackbarMessage } from '@tempo-io/fim3-components';
import type {
  ActivityLogDto,
  ChangeDemandStatusResponseDto,
  CreateDemandCommentDto,
  CreateDemandDto,
  CreateDemandEvidenceResponseDto,
  CreateDemandItemDto,
  CreateDemandItemResponseDto,
  CreateDemandResponseDto,
  CreateDemandReviewerDto,
  DemandCommentDto,
  DemandFilterSchemaDto,
  GetAllDemandsDto,
  GetDemandByIdResponseDto,
  GetDemandCommentResponseDto,
  GetDemandEvidenceResponseDto,
  GetDemandItemsResponseDto,
  GetDemandsResponseDto,
  MatrixDemandSummaryDto,
  UpdateDemandCommentDto,
  UpdateDemandDto,
  UpdateDemandItemDto,
  UpdateDemandItemResponseDto,
  UpdateDemandResponseDto,
} from '@tempo-io/fim3-dto';
import { DescriptionMaxLength } from '@tempo-io/fim3-dto';
import axios, { type AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';
import { demandsQueryKeys } from '@/features/intake/queryKeys/demands';
import { isDescriptionTooLongError } from './descriptionError.utils';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';
import { useS3Api } from './useS3Api';

export type DemandQueryParams = GetAllDemandsDto & {
  filter?: DemandFilterSchemaDto;
  matchingDemandIds?: number[];
};

export const DEMAND_ACTIVITY_PAGE_SIZE = 25;

/**
 * One entry of the demand's Conversation feed. The endpoint collapses comment and attachment
 * history to current state before paging, and stamps each surviving entry with the timestamp it
 * sorts at - a comment stays at its creation time however often it is edited.
 */
export type DemandActivityEntryDto = ActivityLogDto & {
  effectiveAt: string;
};

export type DemandActivityPage = {
  data: DemandActivityEntryDto[];
  total: number;
};

export const useDemandsApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/demands`;
  const s3Api = useS3Api(httpClient);
  const defaultLimit = 25;

  const getDemands = async (params: DemandQueryParams): Promise<GetDemandsResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/getDemands`, params);
    return data;
  };

  const changeDemandStatus = async (
    id: number,
    statusId: number,
  ): Promise<ChangeDemandStatusResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/status`, { statusId });
    return data;
  };

  const getDemand = async (id: number): Promise<GetDemandByIdResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`);
    return data;
  };

  const createDemand = async (demand: CreateDemandDto): Promise<CreateDemandResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}`, demand);
    return data;
  };

  const updateDemand = async (
    id: number,
    demand: UpdateDemandDto,
  ): Promise<UpdateDemandResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}`, demand);
    return data;
  };

  const deleteDemand = async (id: number) => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return { data };
  };

  const getDemandItems = async (demandId: number): Promise<GetDemandItemsResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${demandId}/items`);
    return data;
  };

  const createDemandItem = async (params: {
    demandId: number;
    item: CreateDemandItemDto;
  }): Promise<CreateDemandItemResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/${params.demandId}/items`, params.item);
    return data;
  };

  const updateDemandItem = async (params: {
    demandId: number;
    itemId: number;
    item: UpdateDemandItemDto;
  }): Promise<UpdateDemandItemResponseDto> => {
    const { data } = await httpClient.put(
      `${baseUrl}/${params.demandId}/items/${params.itemId}`,
      params.item,
    );
    return data;
  };

  const deleteDemandItem = async (params: { demandId: number; itemId: number }) => {
    const { data } = await httpClient.delete(
      `${baseUrl}/${params.demandId}/items/${params.itemId}`,
    );
    return { data };
  };

  const getDemandComments = async (demandId: number) => {
    const { data } = await httpClient.get(`${baseUrl}/${demandId}/comments`);

    const userIds = data.data.map((el: DemandCommentDto) => el.commenterId);

    return getUserDataForResults(
      httpClient,
      userIds,
      data.data,
      (item: DemandCommentDto) => item.commenterId ?? '',
    );
  };

  const createDemandComment = async (
    comment: CreateDemandCommentDto & { demandId: number },
  ): Promise<GetDemandCommentResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/${comment.demandId}/comments`, {
      comment: comment.comment,
    });
    return data;
  };

  const updateDemandComment = async (
    comment: UpdateDemandCommentDto & { id: number; demandId: number },
  ): Promise<GetDemandCommentResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${comment.demandId}/comments/${comment.id}`, {
      comment: comment.comment,
    });
    return data;
  };

  const deleteDemandComment = async (params: {
    id: number;
    demandId: number;
  }): Promise<GetDemandCommentResponseDto> => {
    const { data } = await httpClient.delete(`${baseUrl}/${params.demandId}/comment/${params.id}`);
    return data;
  };

  const getDemandEvidence = async (id: number): Promise<GetDemandEvidenceResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/evidence`);
    return data;
  };

  /**
   * The file is uploaded here rather than when it is chosen, so cancelling the dialog leaves nothing
   * behind: an upload lands in the S3 uploads prefix and is only promoted once this POST reaches the
   * server, and nothing sweeps that prefix.
   */
  const createDemandEvidence = async ({
    demandId,
    file,
    ideaId,
    reviewer,
  }: {
    demandId: number;
    file?: File;
    ideaId?: number;
    reviewer?: CreateDemandReviewerDto;
  }): Promise<CreateDemandEvidenceResponseDto> => {
    const attachment = file
      ? {
          name: file.name,
          attachmentUrl: await s3Api.uploadAttachment.mutateAsync(file),
          mimeType: file.type,
          size: file.size,
        }
      : undefined;

    const { data } = await httpClient.post(`${baseUrl}/${demandId}/evidence`, {
      ...(attachment ? { attachment } : {}),
      ...(ideaId !== undefined ? { ideaId } : {}),
      ...(reviewer ? { reviewer } : {}),
    });

    return data;
  };

  const removeDemandEvidence = async (params: { demandId: number; id: number }) => {
    const { data } = await httpClient.delete(`${baseUrl}/${params.demandId}/evidence/${params.id}`);
    return { data };
  };

  const getDemandActivity = async ({
    demandId,
    limit,
    offset,
  }: {
    demandId: number;
    limit: number;
    offset: number;
  }): Promise<DemandActivityPage> => {
    const { data } = await httpClient.get<DemandActivityPage>(`${baseUrl}/${demandId}/activity`, {
      params: { limit, offset },
    });
    return data;
  };

  const useDemandActivity = (demandId: number) =>
    useInfiniteQuery({
      initialPageParam: 0,
      enabled: demandId > 0,
      queryKey: demandsQueryKeys.activity(demandId),
      queryFn: ({ pageParam }) =>
        getDemandActivity({ demandId, limit: DEMAND_ACTIVITY_PAGE_SIZE, offset: pageParam }),
      // Paged off the collapsed `total` rather than a short-page heuristic: the feed collapses
      // comment and attachment history server-side, so a page can legitimately come back short.
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((count, page) => count + page.data.length, 0);
        return loaded < lastPage.total ? loaded : undefined;
      },
    });

  const useGetAll = (params: DemandQueryParams = {}, options: { enabled?: boolean } = {}) => {
    const limit = params.limit ?? defaultLimit;
    const normalizedParams = { ...params, limit };
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      enabled: options.enabled ?? true,
      queryKey: demandsQueryKeys.list(normalizedParams),
      queryFn: ({ pageParam }) => getDemands({ ...params, limit, offset: pageParam * limit }),
      getNextPageParam: (lastPage) =>
        lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined,
    });
  };

  const getMatrixDemands = async ({ params }: { params?: unknown }) => {
    const { data } = await httpClient.post<{
      data: (MatrixDemandSummaryDto & { id: number })[];
    }>(`${baseUrl}/getMatrixDemands`, params);

    return data;
  };

  const useMatrixDemands = (
    viewId?: number,
    params: {
      filter?: DemandFilterSchemaDto;
      search?: string;
      matchingDemandIds?: number[];
    } = {},
    enabled = false,
  ) => {
    const queryKey: QueryKey = [...demandsQueryKeys.matrix(), baseUrl, viewId ?? null, params];

    return useQuery({
      queryKey,
      queryFn: () => getMatrixDemands({ params: { ...params, viewId } }),
      enabled,
    });
  };

  const useGetDemand = (id?: number | null) =>
    useQuery({
      queryKey: demandsQueryKeys.detail(id ?? 0),
      queryFn: () => getDemand(id ?? 0),
      enabled: typeof id === 'number' && id > 0,
    });

  const useGetDemandItems = (demandId: number) =>
    useQuery({
      queryKey: demandsQueryKeys.items(demandId),
      queryFn: () => getDemandItems(demandId),
      enabled: demandId > 0,
    });

  const useDemandComments = ({ id }: { id: number }) =>
    useQuery({
      queryKey: demandsQueryKeys.comments(id),
      queryFn: () => getDemandComments(id),
      enabled: id > 0,
    });

  const useDemandEvidence = (id: number) =>
    useQuery({
      queryKey: demandsQueryKeys.evidence(id),
      queryFn: () => getDemandEvidence(id),
      enabled: id > 0,
    });

  const queryClient = useQueryClient();
  const setSnackbarGlobalError = useSetSnackbarGlobalError();
  const setSnackbarMessage = useSetSnackbarMessage();
  const { t } = useTranslation('translation');

  const useChangeStatus = () =>
    useErrorHandlingMutation<
      ChangeDemandStatusResponseDto,
      unknown,
      { id: number; statusId: number },
      { previous: [readonly unknown[], unknown][] }
    >({
      mutationFn: ({ id, statusId }) => changeDemandStatus(id, statusId),
      onMutate: async ({ id, statusId }) => {
        await queryClient.cancelQueries({ queryKey: demandsQueryKeys.lists() });
        const previous = queryClient.getQueriesData({ queryKey: demandsQueryKeys.lists() });
        queryClient.setQueriesData<InfiniteData<GetDemandsResponseDto>>(
          { queryKey: demandsQueryKeys.lists() },
          (old) =>
            old
              ? {
                  ...old,
                  pages: old.pages.map((page) => ({
                    ...page,
                    data: page.data.map((demand) =>
                      demand.id === id ? { ...demand, statusId } : demand,
                    ),
                  })),
                }
              : old,
        );
        return { previous };
      },
      onError: (error, variables, context) => {
        for (const [key, data] of context?.previous ?? []) {
          queryClient.setQueryData(key, data);
        }
        setSnackbarGlobalError(error, variables);
      },
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.matrix() }),
        ]),
    });

  /**
   * A checklist step is flipped server-side by whichever routine owns its data, so any write that
   * can complete one has to refresh the checklist too - `complete-details` on a demand update,
   * `evidence` on an attachment, `work-items` on an item. Done here rather than at the call sites so
   * a new caller cannot forget it.
   */
  const invalidateChecklist = (demandId: number) =>
    queryClient.invalidateQueries({ queryKey: demandsQueryKeys.checklist(demandId) });

  const invalidateDemandsAndMatrix = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.all() }),
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.matrix() }),
    ]);

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createDemand,
      onSuccess: invalidateDemandsAndMatrix,
      onError: (error: unknown, variables) => {
        if (isDescriptionTooLongError(error)) {
          setSnackbarMessage({
            message: t(($) => $.ideaManagement.errors.descriptionTooLong, {
              max: DescriptionMaxLength.toLocaleString(),
            }),
            severity: 'error',
          });
          return;
        }
        setSnackbarGlobalError(error, variables);
      },
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: ({ id, demand }: { id: number; demand: UpdateDemandDto }) =>
        updateDemand(id, demand),
      onMutate: async ({ id, demand }) => {
        await queryClient.cancelQueries({ queryKey: demandsQueryKeys.detail(id) });
        const previous = queryClient.getQueryData<GetDemandByIdResponseDto>(
          demandsQueryKeys.detail(id),
        );
        if (previous?.data) {
          queryClient.setQueryData<GetDemandByIdResponseDto>(demandsQueryKeys.detail(id), {
            data: { ...previous.data, ...demand },
          });
        }
        return { previous };
      },
      onError: (error, variables, context) => {
        if (context?.previous) {
          queryClient.setQueryData(demandsQueryKeys.detail(variables.id), context.previous);
        }

        if (isDescriptionTooLongError(error)) {
          setSnackbarMessage({
            message: t(($) => $.ideaManagement.errors.descriptionTooLong, {
              max: DescriptionMaxLength.toLocaleString(),
            }),
            severity: 'error',
          });
          return;
        }

        setSnackbarGlobalError(error, variables);
      },
      onSettled: (_data, _error, variables) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.matrix() }),
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.detail(variables.id) }),
          invalidateChecklist(variables.id),
        ]),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemand,
      onSuccess: invalidateDemandsAndMatrix,
    });

  // Field-level 400/503 handling lives in the Work Item Hierarchy dialog, so these two mutations
  // deliberately skip useErrorHandlingMutation's default global-snackbar onError - it would
  // otherwise show a generic toast alongside (or instead of) the dialog's inline/retry UI.
  // The `work-items` step completes on item count, so create and delete move it; an in-place
  // update of an existing item cannot change the count and so leaves the step alone.
  const invalidateItems = (demandId: number) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.items(demandId) }),
      invalidateChecklist(demandId),
    ]);

  const useCreateDemandItem = () =>
    useMutation({
      mutationFn: createDemandItem,
      onSuccess: (_data, variables) => invalidateItems(variables.demandId),
    });

  const useUpdateDemandItem = () =>
    useMutation({
      mutationFn: updateDemandItem,
      onSuccess: (_data, variables) =>
        queryClient.invalidateQueries({ queryKey: demandsQueryKeys.items(variables.demandId) }),
    });

  const useDeleteDemandItem = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemandItem,
      onSuccess: (_data, variables) => invalidateItems(variables.demandId),
    });

  const invalidateActivity = (demandId: number) =>
    queryClient.invalidateQueries({ queryKey: demandsQueryKeys.activity(demandId) });

  const invalidateComments = (demandId: number) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.comments(demandId) }),
      invalidateActivity(demandId),
    ]);

  const useCreateComment = () =>
    useErrorHandlingMutation({
      mutationFn: createDemandComment,
      onSuccess: (_data, variables) => invalidateComments(variables.demandId),
    });

  const useUpdateComment = () =>
    useErrorHandlingMutation({
      mutationFn: updateDemandComment,
      onSuccess: (_data, variables) => invalidateComments(variables.demandId),
    });

  const useDeleteComment = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemandComment,
      onSuccess: (_data, variables) => invalidateComments(variables.demandId),
    });

  // Four keys, not one: the row is new, a reviewer may have been created with it, the feed gained an
  // entry, and adding evidence reverts an existing sufficient mark - so the checklist has to be
  // refetched even though the client is not the one flipping the step.
  const invalidateEvidence = (demandId: number) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.evidence(demandId) }),
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.reviewers(demandId) }),
      invalidateActivity(demandId),
      invalidateChecklist(demandId),
    ]);

  const addEvidence = useErrorHandlingMutation({
    mutationFn: createDemandEvidence,
    onSuccess: (_data, variables) => invalidateEvidence(variables.demandId),
    // A 409 means the named reviewer already reviews this demand, and the whole add was rolled back -
    // including the uploaded file. Saying "adding evidence failed" would hide the one thing the user
    // has to change before retrying.
    onError: (error, variables) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setSnackbarMessage({
          message: t(($) => $.ideaManagement.general.demands.panel.evidence.reviewerConflict),
          severity: 'error',
        });
        return;
      }
      setSnackbarGlobalError(error, variables);
    },
  });

  const removeEvidence = useErrorHandlingMutation({
    mutationFn: removeDemandEvidence,
    onSuccess: (_data, variables) => invalidateEvidence(variables.demandId),
  });

  return {
    getDemands,
    useGetAll,
    useMatrixDemands,
    useGetDemand,
    useDemandComments,
    useDemandActivity,
    useChangeStatus,
    useCreate,
    useUpdate,
    useDelete,
    useGetDemandItems,
    useCreateDemandItem,
    useUpdateDemandItem,
    useDeleteDemandItem,
    useCreateComment,
    useUpdateComment,
    useDeleteComment,
    useDemandEvidence,
    addEvidence,
    removeEvidence,
  };
};
